import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dataFlowLog, summarizeCollection } from '../utils/dataFlowLogger';
import queryKeys from '../queries/queryKeys';
import useQueryScope from '../queries/useQueryScope';
import { ownsQueryScope } from '../queries/queryOwnership';

const defaultActivitiesContext = {
  activities: [],
  loading: false,
  setActivities: () => {},
  fetchActivities: async () => {},
  createActivity: async () => {},
  removeActivity: () => {},
  updateActivity: () => {},
};

const ActivitiesContext = createContext(defaultActivitiesContext);

export const useActivitiesContext = () => useContext(ActivitiesContext);


const coerceActivitiesArray = (val) => {
  if (Array.isArray(val)) return val;
  if (Array.isArray(val?.data)) return val.data;
  if (Array.isArray(val?.items)) return val.items;
  return [];
};

const getActivityId = (activity) => activity?._id || activity?.id;

export const ActivitiesProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { scope, isCurrent, enabled, hasProvider } = useQueryScope();
  const activitiesQueryKey = useMemo(() => queryKeys.activities.list({ scope: 'context', ...scope }), [scope]);
  const activitiesQuery = useQuery({
    queryKey: activitiesQueryKey,
    queryFn: async () => [],
    enabled: false,
    initialData: [],
  });
  const activities = useMemo(
    () => coerceActivitiesArray(activitiesQuery.data),
    [activitiesQuery.data],
  );

  const setActivities = useCallback((itemsOrUpdater, options = {}) => {
    if (options.scope ? !ownsQueryScope(options.scope) : (!isCurrent() || (hasProvider && !enabled))) return;
    const targetKey = options.scope ? queryKeys.activities.list({ scope: 'context', ...options.scope }) : activitiesQueryKey;
    const currentItems = coerceActivitiesArray(queryClient.getQueryData(targetKey));
    const resolvedItems = typeof itemsOrUpdater === 'function'
      ? itemsOrUpdater(currentItems)
      : itemsOrUpdater;

    dataFlowLog('activities-context.set.request', {
      next: typeof itemsOrUpdater === 'function'
        ? 'updater'
        : summarizeCollection(coerceActivitiesArray(itemsOrUpdater)),
    });
    queryClient.setQueryData(targetKey, coerceActivitiesArray(resolvedItems));
  }, [queryClient, activitiesQueryKey, isCurrent, enabled, hasProvider]);

  const fetchActivities = useCallback(async () => {
    if (!isCurrent() || (hasProvider && !enabled)) return [];
    const currentItems = coerceActivitiesArray(queryClient.getQueryData(activitiesQueryKey));
    queryClient.setQueryData(activitiesQueryKey, currentItems);
    return currentItems;
  }, [queryClient, activitiesQueryKey, isCurrent, enabled, hasProvider]);

  const createActivity = useCallback(async (activity) => {
    setActivities((current) => [...current, activity]);
    return activity;
  }, [setActivities]);

  const removeActivity = useCallback((id) => {
    setActivities((current) => current.filter((activity) => getActivityId(activity) !== id));
  }, [setActivities]);

  const updateActivity = useCallback((activity) => {
    const updatedId = getActivityId(activity);
    if (!updatedId) return;

    setActivities((current) => {
      const idx = current.findIndex((item) => getActivityId(item) === updatedId);
      if (idx === -1) return [...current, activity];

      const next = current.slice();
      next[idx] = { ...current[idx], ...activity };
      return next;
    });
  }, [setActivities]);

  const value = useMemo(() => ({
    activities,
    queryKey: activitiesQueryKey,
    loading: activitiesQuery.isLoading,
    setActivities,
    fetchActivities,
    createActivity,
    removeActivity,
    updateActivity,
  }), [
    activities,
    activitiesQueryKey,
    activitiesQuery.isLoading,
    setActivities,
    fetchActivities,
    createActivity,
    removeActivity,
    updateActivity,
  ]);

  useEffect(() => {
    dataFlowLog('activities-context.updated', {
      loading: activitiesQuery.isLoading,
      activities: summarizeCollection(activities),
    });
  }, [activities, activitiesQuery.isLoading]);

  return (
    <ActivitiesContext.Provider value={value}>
      {children}
    </ActivitiesContext.Provider>
  );
};
